import { useMemo } from 'react'
import { BookOpen } from 'lucide-react'
import { Emoji } from '@components/Emoji.tsx'
import { useResource } from '@hooks/useResource.ts'
import { Link } from '@router'
import { api } from '@api'
import type { MealPlanDay } from '@hewey/shared'
import { addDays, toIsoDate } from './dates.ts'
import styles from './MealsWidget.module.css'

export const MealsWidget = () => {
	const [today, tomorrow] = useMemo(() => {
		const d = new Date()
		return [toIsoDate(d), toIsoDate(addDays(d, 1))]
	}, [])

	const resource = useResource(['meals-widget', today, tomorrow], () =>
		api.mealPlan.list(today, tomorrow),
	)

	const dayMap = useMemo(() => {
		const m = new Map<string, MealPlanDay>()
		for (const d of resource.data?.days ?? []) m.set(d.date, d)
		return m
	}, [resource.data])

	const todayDay = dayMap.get(today) ?? null
	const tomorrowDay = dayMap.get(tomorrow) ?? null
	const loading = resource.loading && !resource.data

	return (
		<div className={styles.root}>
			<header className={styles.header}>
				<span className={styles.emoji}>
					<Emoji char="🍲" label="Meals" />
				</span>
				<span className={styles.heading}>Meals</span>
			</header>
			<div className={styles.rows}>
				<div className={styles.row}>
					<span className={styles.rowLabel}>Today</span>
					{loading ? (
						<span className={styles.placeholder}>…</span>
					) : todayDay?.title ? (
						todayDay.recipeId ? (
							<Link
								href={`/recipes/${todayDay.recipeId}`}
								className={styles.recipeLink}
								onClick={(e) => e.stopPropagation()}
							>
								<BookOpen size={14} aria-hidden />
								<span className={styles.rowTitle}>
									{todayDay.title}
								</span>
							</Link>
						) : (
							<span className={styles.rowTitle}>
								{todayDay.title}
							</span>
						)
					) : (
						<span className={styles.placeholder}>
							No meal planned
						</span>
					)}
				</div>
				<div className={`${styles.row} ${styles.rowMuted}`}>
					<span className={styles.rowLabel}>Tomorrow</span>
					{loading ? (
						<span className={styles.placeholder}>…</span>
					) : tomorrowDay?.title ? (
						<span className={styles.rowTitle}>
							{tomorrowDay.title}
						</span>
					) : (
						<span className={styles.placeholder}>—</span>
					)}
				</div>
			</div>
		</div>
	)
}
